import { useState, useEffect } from 'react';

/**
 * Returns a live minute countdown from the current time to a UTC target.
 * Recomputes every 60 seconds. Returns null if no target is provided.
 *
 * API timestamps may lack a 'Z' suffix — this hook normalizes them to UTC.
 */
const toUtcDate = (s: string): Date => {
    const normalized = s.endsWith('Z') || s.includes('+') ? s : s + 'Z';
    return new Date(normalized);
};

export const useUtcCountdown = (utcTimestamp: string | null | undefined): number | null => {
    const [minutes, setMinutes] = useState<number | null>(null);

    useEffect(() => {
        if (!utcTimestamp) {
            setMinutes(null);
            return;
        }

        const compute = () => Math.max(0, Math.ceil((toUtcDate(utcTimestamp).getTime() - Date.now()) / 60000));

        setMinutes(compute());
        const interval = setInterval(() => setMinutes(compute()), 60000);
        return () => clearInterval(interval);
    }, [utcTimestamp]);

    return minutes;
};

/** Compute static duration in minutes between two UTC timestamps. */
export const computeUtcDiffMinutes = (startUtc: string | null | undefined, endUtc: string | null | undefined): number | null => {
    if (!startUtc || !endUtc) return null;
    const diff = Math.ceil((toUtcDate(endUtc).getTime() - toUtcDate(startUtc).getTime()) / 60000);
    return diff > 0 ? diff : null;
};

/** Format a UTC timestamp (possibly missing Z suffix) to locale string. */
export const formatUtcDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '—';
    return toUtcDate(dateStr).toLocaleString();
};
